<template>
  <div class="rate-tiers-cell" :class="{ 'is-inactive': isInactive }" :style="gridStyle">

    <!-- XOL-498: band caption. ABOVE the header it explains, and LEFT-aligned
         on purpose — the two notes below the grid (.rtc-overflow-note,
         .rtc-more-rates) are right-aligned and both about the drawer, so a
         third right-aligned grey line would join that cluster and read as a
         third drawer affordance. This is a different kind of statement.

         A plain <div>, NOT a .rtc-grid row: RateVersionManager overrides the
         grid's track template via ::v-deep (36px 160px …), so a caption built
         as a grid row would indent under the Tenor column in the drawer.

         Deliberately NOT gated on hideDrawerAffordance. Three of the four
         consumers pass that prop, so gating would leave the caption on the
         rates list alone — silently collapsing this into the list-only
         placement that was considered and rejected. -->
    <div v-if="bandCaption" class="rtc-band-note">{{ bandCaption }}</div>

    <!-- In-cell header row: Tier | Tenor | first 3 in-band positions [next 3 at lg] -->
    <div class="rtc-grid rtc-header">
      <span class="rtc-col-tier">Tier</span>
      <span class="rtc-col-tenor">Tenor</span>
      <!-- Header/value association is load-bearing: this span and the value
           span at the same rendered index (below) must key rtc-lg-only off
           the SAME idx of the SAME ratePositions array, or a header can end
           up over the wrong value after a hide/reveal. No aria-label added:
           a role-less <span> isn't reliably announced, and no value in this
           grid is programmatically associated with any header, band or not —
           labelling headers alone would be cosmetic (XOL-487 UX spec, a11y). -->
      <span
        v-for="(pos, idx) in ratePositions"
        :key="pos"
        class="rtc-col-rate rtc-rate-header"
        :class="{ 'rtc-lg-only': idx >= 3 }">#{{ pos }}</span>
    </div>

    <!-- Tier rows (capped at MAX_VISIBLE_TIERS=5; scrollable when overflow) -->
    <div class="rtc-tiers-region" :class="{ 'rtc-scrollable': hasOverflow }">
      <div
        v-for="tier in visibleTiers"
        :key="tier.seqNo"
        class="rtc-grid rtc-tier-row">

        <span class="rtc-col-tier">
          <span class="tier-tag" :class="tier.seqNo === 1 ? 'tier-1' : 'tier-n'">T{{ tier.seqNo }}</span>
        </span>

        <span class="rtc-col-tenor">{{ tenorLabel(tier) }}</span>

        <span
          v-for="(pos, idx) in ratePositions"
          :key="pos"
          class="rtc-col-rate"
          :class="{ r1: idx === 0, 'rtc-lg-only': idx >= 3 }">{{ formatRate(tier['telco' + pos]) }}</span>

      </div>
    </div>

    <!-- Overflow note (>5 tiers) -->
    <div v-if="hasOverflow && !hideDrawerAffordance" class="rtc-overflow-note">
      Tiers T6&#8211;T{{ lastTierSeqNo }} in drawer
    </div>

    <!-- +N more in drawer: in-band positions past the 6 the grid can show -->
    <div v-if="tiers.length > 0 && hiddenRates > 0 && !hideDrawerAffordance" class="rtc-more-rates">
      +{{ hiddenRates }} more in drawer
    </div>

  </div>
</template>

<script>
import { bandPositions, visibleRatePositions, hiddenRateCount, RATE_COLS_NARROW, RATE_POSITION_MAX } from '@/lib/rateBand'

var MAX_VISIBLE_TIERS = 5

export default {
  name: 'rate-tiers-cell',

  props: {
    rowData: {
      type: Object,
      required: true
    },

    hideDrawerAffordance: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Derive normalized tiers from rowData — computed so prop changes re-render
    // (row-recycle guard: el-table reuses cells across pagination/sort)
    tiers: function () {
      var row = this.rowData
      if (!row) return []

      var tiersArr = row.tiers
      if (tiersArr && Array.isArray(tiersArr) && tiersArr.length > 0) {
        return tiersArr.slice().sort(function (a, b) {
          return Number(a.seqNo) - Number(b.seqNo)
        }).map(function (t) {
          return {
            seqNo: Number(t.seqNo),
            fromMonth: Number(t.fromMonth) || 0,
            toMonth: Number(t.toMonth) || 0,
            telco1: Number(t.telco1) || 0,
            telco2: Number(t.telco2) || 0,
            telco3: Number(t.telco3) || 0,
            telco4: Number(t.telco4) || 0,
            telco5: Number(t.telco5) || 0,
            telco6: Number(t.telco6) || 0,
            telco7: Number(t.telco7) || 0,
            telco8: Number(t.telco8) || 0,
            telco9: Number(t.telco9) || 0,
            telco10: Number(t.telco10) || 0
          }
        })
      }

      // Flat fallback: synthesize a single Tier 1 from flat root columns
      return [{
        seqNo: 1,
        fromMonth: Number(row.fromMonth) || 0,
        toMonth: Number(row.toMonth) || 0,
        telco1: Number(row.telco1) || 0,
        telco2: Number(row.telco2) || 0,
        telco3: Number(row.telco3) || 0,
        telco4: Number(row.telco4) || 0,
        telco5: Number(row.telco5) || 0,
        telco6: Number(row.telco6) || 0,
        telco7: Number(row.telco7) || 0,
        telco8: Number(row.telco8) || 0,
        telco9: Number(row.telco9) || 0,
        telco10: Number(row.telco10) || 0
      }]
    },

    // XOL-487: which absolute Rate #N positions this row's band renders,
    // capped to what the grid can show (RATE_COLS_MAX). Computed off the
    // rowData prop — never data()/mounted — so el-table's cell recycling
    // across sort/pagination re-derives this on every prop change instead of
    // keeping a stale band from a previously-recycled row.
    ratePositions: function () {
      return visibleRatePositions(this.rowData)
    },

    // In-band positions past what the grid can show — drives "+N more in
    // drawer". Same band rule as ratePositions, so the two can never disagree.
    hiddenRates: function () {
      return hiddenRateCount(this.rowData)
    },

    // XOL-498: the sentence that explains why the grid opens at #2 instead of
    // #1. Since XOL-487 this cell renders only the positions a rate prices,
    // which is correct and completely unexplained — the column headers are the
    // only clue, and they read as data rather than as a restriction.
    //
    // Derived from bandPositions(), NOT from raw fromSharer/toSharer. A rate
    // with `toSharer: null` means "no upper bound"; read raw it would print
    // "#2–#null". bandPositions() resolves that to RATE_POSITION_MAX, and
    // collapses absent/default/null onto the same [1..10].
    //
    // Which is also the visibility gate (Carlos, 2026-09-14: only when
    // non-default). A full-span band says nothing a reader needs, and
    // "Prices Rate #1–#10 only" would assert a restriction that is not there.
    // One length check silences default, absent and null-ceiling together —
    // deliberately NOT distinguishing "band not recorded" from "prices
    // everything", because nothing in the SPA can act on the difference and
    // that population only shrinks (every version published since XOL-500
    // carries its own band).
    //
    // Note this is the WHOLE band, not `ratePositions` — that one is capped at
    // RATE_COLS_MAX for layout. A rate banded 2..10 must say "#2–#10 only"
    // while the grid shows #2–#7 and the existing "+3 more in drawer" note
    // explains the rest. Two different statements; using ratePositions here
    // would make the caption contradict the band.
    bandCaption: function () {
      var pos = bandPositions(this.rowData)
      if (pos.length === RATE_POSITION_MAX) return ''
      if (pos.length === 1) return 'Prices Rate #' + pos[0] + ' only.'
      // Literal en-dash U+2013 and a `#` on both numbers, matching
      // SharerRateVersion's lgHintFor — an HTML entity would not survive
      // interpolation.
      return 'Prices Rate #' + pos[0] + '–#' + pos[pos.length - 1] + ' only.'
    },

    // CSS custom properties only — never an inline grid-template-columns; see
    // the .rtc-grid rule below for why (::v-deep drawer override). Values are
    // STRINGS: a bare number in a :style object gets px-suffixed by Vue,
    // which would break inside repeat(N, 1fr).
    gridStyle: function () {
      var cols = this.ratePositions.length
      var narrow = Math.min(cols, RATE_COLS_NARROW)
      return {
        '--rtc-cols': String(cols),
        '--rtc-cols-narrow': String(narrow)
      }
    },

    visibleTiers: function () {
      return this.tiers.slice(0, MAX_VISIBLE_TIERS)
    },

    hasOverflow: function () {
      return this.tiers.length > MAX_VISIBLE_TIERS
    },

    lastTierSeqNo: function () {
      if (this.tiers.length === 0) return 0
      return this.tiers[this.tiers.length - 1].seqNo
    },

    isInactive: function () {
      return this.rowData ? this.rowData.status !== 'Active' : false
    }
  },

  methods: {
    tenorLabel: function (tier) {
      var from = Number(tier.fromMonth) || 0
      var to = Number(tier.toMonth) || 0
      var diff = Math.max(0, to - from)
      var years = (diff / 12).toFixed(1)
      // en-dash U+2013 between months; spaced middot U+00B7 between groups
      return from + '–' + to + ' mo · ' + years + ' yr'
    },

    formatRate: function (value) {
      var n = Number(value)
      if (isNaN(n) || n === 0) return '–'
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    }
  }
}
</script>

<style scoped>
.rate-tiers-cell {
  font-size: 12px;
  line-height: 1.5;
  min-width: 0;
}

/* Base grid: Tier | Tenor | first 3 rendered rate columns (lg-only cols are
   display:none at md/sm). Tier + Tenor are FIXED widths (not auto) so the
   header row and every tier row — which are independent grids — share
   identical track geometry; otherwise the header's short "Tenor" vs a row's
   long tenor label give the 1fr rate columns different widths and the #N
   headers drift off their values (XOL-319).

   XOL-487: the rate-column count is now band-derived (--rtc-cols /
   --rtc-cols-narrow, set inline per-row by gridStyle — see the script). Each
   var-based declaration is preceded by a static repeat(N, 1fr) fallback so a
   non-supporting engine degrades to today's fixed 3/6-column layout instead
   of a collapsed grid — the component already requires modern Grid
   (display:grid, repeat(), gap), so custom properties exclude nothing new.
   Deliberately NOT an inline grid-template-columns: RateVersionManager.vue
   overrides the leading two tracks (36px 160px vs 32px 116px) via
   ::v-deep .rtc-grid because the drawer is wider; an inline declaration on
   the root would beat that stylesheet rule and silently kill the override. */
.rtc-grid {
  display: grid;
  grid-template-columns: 32px 116px repeat(3, 1fr);
  grid-template-columns: 32px 116px repeat(var(--rtc-cols-narrow, 3), 1fr);
  gap: 0 4px;
  align-items: baseline;
}

/* XOL-498: band caption — "Prices Rate #2–#5 only."
   Three declarations, all reusing values already in this file: the micro-text
   colour and size shared by .rtc-header / .rtc-overflow-note / .rtc-more-rates,
   and .rtc-header's own bottom margin. No new token, no border, no background.
   Not italic — that is .rtc-rate-header's only job here. */
.rtc-band-note {
  color: #8492a6;
  font-size: 11px;
  margin-bottom: 3px;
}

/* Header row */
.rtc-header {
  color: #8492a6;
  font-size: 11px;
  margin-bottom: 3px;
  border-bottom: 1px solid #ebeef5;
  padding-bottom: 2px;
}

.rtc-rate-header {
  font-style: italic;
  text-align: right;
}

/* Tier tag tokens (mirror SharerRateTierCard.vue left-bar tokens) */
.tier-tag {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
}

.tier-1 {
  background: #f0f9eb;
  color: #67c23a;
}

.tier-n {
  background: #f4f4f5;
  color: #909399;
}

/* Column types */
.rtc-col-tier {
  white-space: nowrap;
}

.rtc-col-tenor {
  white-space: nowrap;
  padding-right: 6px;
  color: #606266;
  font-size: 11px;
}

.rtc-col-rate {
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #303133;
  font-size: 12px;
}

/* First rendered rate column — slightly bolder (leftmost in-band position, not necessarily #1) */
.r1 {
  font-weight: 500;
}

/* lg-only columns: rendered index >= 3 — shown only at ≥1200px */
.rtc-lg-only {
  display: none;
}

@media (min-width: 1200px) {
  .rtc-grid {
    grid-template-columns: 32px 116px repeat(6, 1fr);
    grid-template-columns: 32px 116px repeat(var(--rtc-cols, 6), 1fr);
  }

  .rtc-lg-only {
    display: block; /* reveal rendered index >= 3 header + cells */
  }
}

/* Scrollable tiers region when >5 tiers */
.rtc-tiers-region.rtc-scrollable {
  max-height: 200px;
  overflow-y: auto;
}

/* Overflow note: "Tiers T6–T{last} in drawer" */
.rtc-overflow-note {
  text-align: right;
  color: #8492a6;
  font-size: 11px;
  margin-top: 3px;
}

/* "+N more in drawer" */
.rtc-more-rates {
  text-align: right;
  color: #8492a6;
  font-size: 11px;
  margin-top: 2px;
}

/* Inactive: grey the cell (can't reach tr — greys own td only) */
/* XOL-498: .rtc-band-note is in this list for completeness, not for effect —
   it is already #8492a6, so today this changes nothing. The list is exhaustive
   by convention, and the next person to darken the caption token would
   otherwise ship a full-strength note over a greyed-out cell. */
.is-inactive .rtc-col-tenor,
.is-inactive .rtc-col-rate,
.is-inactive .rtc-col-tier,
.is-inactive .rtc-header,
.is-inactive .rtc-band-note,
.is-inactive .rtc-overflow-note,
.is-inactive .rtc-more-rates {
  color: #8492a6 !important;
}

.is-inactive .tier-1,
.is-inactive .tier-n {
  background: #f4f4f5;
  color: #8492a6 !important;
}
</style>
